import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import logo from "assets/images/logo_trans.png";
import classes from "./Header.module.css";
import HeaderMenu from "components/HeaderMenu/HeaderMenu";
import UserAccount from "components/UserAccount/UserAccount";
import EmailIcon from "@mui/icons-material/Email";
import PropTypes from "prop-types";
import PollIcon from "@mui/icons-material/Poll";
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import CampaignIcon from "@mui/icons-material/Campaign";
import {
  Dialog,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Slide,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import ReleaseUpdates from "components/ReleaseUpdates/ReleaseUpdates";
import { Box } from "@mui/system";
import { getConfigurationValues } from "services/userServices";
import { useDispatch, useSelector } from "react-redux";
import { generalStoreActions } from "Store/GeneralStore";
import BackdropComponent from "components/Spinner/Spinner";
import { Language, ManageAccounts } from "@mui/icons-material";
import AdminManagement from "components/AdminManagement/AdminManagement";
import { ADMIN_ACCESS } from "config/CONSTANTS";
import { SnackBarStoreAction } from "Store/SnackBar";
import DeveloperMode from "components/DeveloperMode/DeveloperMode";
export const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Header = (props) => {
  const { t } = useTranslation(["header"], { useSuspense: false });
  const generalStore = useSelector((state) => state.generalStore);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showIcon, setShowIcon] = useState(false);
  const [enableAdminManagement, setEnableAdminManagement] = useState(false);
  const [newUpdatesAvailable, setNewUpdatesAvailable] = useState(false);
  const [openUpdatesModal, setOpenUpdatesModal] = useState(false);
  const [openAdminModal, setOpenAdminModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const doNotShowReleaseNotesAgain =
    localStorage.getItem("doNotShowReleaseNotesAgain") === "true";
  const [anchorEl, setAnchorEl] = useState(null);
  const [showLanguageTranslation, setShowLanguageTranslation] = useState(false);

  const open = Boolean(anchorEl);

  const languageOptions = [
    { code: "en", label: "English (US)" },
    { code: "fr", label: "Français (Canada)" },
    { code: "pt", label: "Português (Brasil)" },
    { code: "es", label: "Español" },
  ];

  const [selectedLangCode, setSelectedLangCode] = useState(
    (localStorage.getItem("i18nextLng") === "en-US"
      ? "en"
      : localStorage.getItem("i18nextLng")) || "en",
  );

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (lang) => {
    i18next.changeLanguage(lang);
    setSelectedLangCode(lang);
    handleClose();
  };

  const showSnack = React.useCallback(
    (message, severity) => {
      const obj = {
        open: true,
        message: message,
        severity: severity,
      };
      dispatch(SnackBarStoreAction.updateSnack(obj));
    },
    [dispatch],
  );

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleReleaseNotesDisplay = React.useCallback(
    (displayNewReleaseNotesUpto) => {
      const today = new Date();
      const displayNewReleaseNotesUptoDate = new Date(
        displayNewReleaseNotesUpto,
      );
      if (today.getTime() <= displayNewReleaseNotesUptoDate.getTime()) {
        setNewUpdatesAvailable(true);
        if (!doNotShowReleaseNotesAgain) setOpenUpdatesModal(true);
      }
    },
    [doNotShowReleaseNotesAgain],
  );

  useEffect(() => {
    const adminIds = ADMIN_ACCESS.split(";");
    const userEmail = localStorage.getItem("EmailId");
    const access = adminIds.includes(userEmail); //true / false
    setEnableAdminManagement(access);
  }, [
    dispatch,
    generalStore.displayNewReleaseNotesUpto,
    handleReleaseNotesDisplay,
    showSnack,
  ]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (!cancelled) {
          setLoading(true);
        }

        const response = await getConfigurationValues(
          "appsettings:DisplayNewReleaseNotesUpto",
        );

        if (cancelled) return;

        if (response?.statusCode === 200) {
          const displayNewReleaseNotesUpto =
            response?.data?.DisplayNewReleaseNotesUpto;

          if (!cancelled) {
            dispatch(
              generalStoreActions.setDisplayNewReleaseUpto(
                displayNewReleaseNotesUpto,
              ),
            );
            handleReleaseNotesDisplay(displayNewReleaseNotesUpto);
            setLoading(false);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setLoading(false);
          showSnack("Unable to login, Authentication error occured.", "error");
          console.log(e);
        }
      }
    };

    if (generalStore.displayNewReleaseNotesUpto === null) {
      fetchData();
    } else {
      handleReleaseNotesDisplay(generalStore.displayNewReleaseNotesUpto);
    }

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    generalStore.displayNewReleaseNotesUpto,
    handleReleaseNotesDisplay,
    showSnack,
  ]);

  useEffect(() => {
    if (
      window.location.host !== "astt.ttec.com" ||
      window.location.host !== "app-astt-ui-uat.azurewebsites.net" ||
      window.location.host !== "app-astt-ui-qa.azurewebsites.net"
    ) {
      setShowIcon(true);
    }
  }, []);

  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname;
    const isHomeRoute =
      currentPath.includes("/home") || currentPath.includes("/bulkedit");
    setShowLanguageTranslation(isHomeRoute);

    if (!isHomeRoute) {
      i18next.changeLanguage("en");
      setSelectedLangCode("en");
    }
  }, [location.pathname]);

  const iconButtonStyle = {
    color: "#3299FF",
    position: "relative",
    "& svg": {
      fontSize: 28,
    },
    "&:hover": {
      transform: "scale(1.1)",
      backgroundColor: "rgba(50, 153, 255, 0.1)",
    },
    transition: "all 0.2s ease-in-out",
  };

  const releaseIconStyle = (highlight) => ({
    color: highlight ? "#fff" : "#3299FF",
    position: "relative",
    "& svg": {
      fontSize: highlight ? 26 : 28,
    },
    "&:hover": {
      transform: "scale(1.1)",
      backgroundColor: highlight
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(50, 153, 255, 0.1)",
    },
    transition: "all 0.2s ease-in-out",
  });

  const gradientWrapperStyle = {
    display: "flex",
    background:
      "linear-gradient(45deg, #4D0033 -16.83%, #660033 20%, #660066 40%, #1A004D 60%, #003366 80%, #006633 104.1%)",
    borderRadius: "50%",
    position: "relative",
    overflow: "hidden",

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(255, 255, 255, 0) 100%)",
      animation: "shine 2s infinite",
      zIndex: 1,
    },

    "& .MuiIconButton-root": {
      position: "relative",
      zIndex: 2,
    },

    // Define keyframes within the sx prop
    "@keyframes shine": {
      "0%": {
        left: "-100%",
      },
      "100%": {
        left: "100%",
      },
    },
  };

  if (props.version === "new")
    return (
      <>
        {loading ? <BackdropComponent /> : null}
        <Paper
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#fff",
            color: "#3299ff",
            px: 2,
            boxShadow: "0 8px 8px rgba(0, 0, 0, 0.1)",
            height: "5rem",
            borderRadius: "0.5rem",
            transition: "width 0.3s ease-in-out",
          }}
        >
          <Typography
            component="h1"
            variant="h6"
            sx={{
              cursor: "pointer",
              fontFamily: "Urbanist, sans-serif",
              fontWeight: 700,
              fontSize: {
                xs: "1.5rem", // smaller screens
                sm: "1.75rem",
                md: "2rem", // medium and up
              },
              letterSpacing: "0.10em", // slightly reduced for better word shape
              color: "#3299ff",
              lineHeight: 1.2,
              userSelect: "none", // avoid accidental text selection
              transition: "all 0.35s ease-in-out",
              "&:hover": {
                color: "#2878cc",
              },
            }}
          >
            {props.title}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {showLanguageTranslation && (
              <>
                <Tooltip
                  title={
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "1rem",
                      }}
                    >
                      {t("changeLanguage")}
                    </Typography>
                  }
                  arrow
                >
                  <IconButton sx={iconButtonStyle} onClick={handleOpen}>
                    <Box>
                      <Language />
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 0.25,
                          fontSize: "0.75rem",
                          letterSpacing: "0.08em",
                          fontWeight: "bold",
                          color: "#3299FF",
                        }}
                      >
                        {selectedLangCode.toUpperCase()}
                      </Typography>
                    </Box>
                  </IconButton>
                </Tooltip>

                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                  {languageOptions.map(({ code, label }) => (
                    <MenuItem
                      key={code}
                      onClick={() => handleSelect(code)}
                      selected={code === selectedLangCode}
                    >
                      <ListItemIcon>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "1rem", color: "#3299FF" }}
                        >
                          {label}
                        </Typography>
                      </ListItemIcon>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {enableAdminManagement && (
              <Tooltip
                title={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "1rem",
                    }}
                  >
                    {t("adminManagement")}
                  </Typography>
                }
                arrow
              >
                <IconButton
                  sx={iconButtonStyle}
                  onClick={() => setOpenAdminModal(true)}
                >
                  <ManageAccounts />
                </IconButton>
              </Tooltip>
            )}

            {newUpdatesAvailable ? (
              <Box sx={gradientWrapperStyle}>
                <Tooltip
                  title={
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "1rem",
                      }}
                    >
                      {t("releaseUpdate")}
                    </Typography>
                  }
                  arrow
                >
                  <IconButton
                    sx={releaseIconStyle(newUpdatesAvailable)}
                    onClick={() => setOpenUpdatesModal(true)}
                  >
                    <CampaignIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Tooltip
                title={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "1rem",
                    }}
                  >
                    {t("releaseUpdate")}
                  </Typography>
                }
                arrow
              >
                <IconButton
                  sx={releaseIconStyle(newUpdatesAvailable)}
                  onClick={() => setOpenUpdatesModal(true)}
                >
                  <CampaignIcon />
                </IconButton>
              </Tooltip>
            )}

            {showIcon && (
              <Tooltip
                title={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "1rem",
                    }}
                  >
                    {t("openSurvey")}
                  </Typography>
                }
                arrow
              >
                <IconButton
                  sx={iconButtonStyle}
                  onClick={() =>
                    window.open(
                      `https://teletechinc.sharepoint.com/sites/zzDevTest/AnuranjanTest/Lists/Agent%20State%20Tracker/NewForm.aspx?Source=https%3A%2F%2Fteletechinc%2Esharepoint%2Ecom%2Fsites%2FzzDevTest%2FAnuranjanTest%2FLists%2FAgent%2520State%2520Tracker%2Foverview%2Easpx`,
                      "_blank",
                    )
                  }
                >
                  <SentimentVerySatisfiedIcon />
                </IconButton>
              </Tooltip>
            )}

            {showIcon && (
              <Tooltip
                title={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "1rem",
                    }}
                  >
                    {t("ASTT Intelligence")}
                  </Typography>
                }
                arrow
              >
                <IconButton
                  sx={iconButtonStyle}
                  onClick={() => navigate("/biReport")}
                >
                  <PollIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip
              title={
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "1rem",
                  }}
                >
                  {t("notificationCenter")}
                </Typography>
              }
              arrow
            >
              <IconButton sx={iconButtonStyle}>
                <EmailIcon />
              </IconButton>
            </Tooltip>
            <DeveloperMode />
            <HeaderMenu />
            <UserAccount />
          </Box>
        </Paper>
        <Dialog
          open={openUpdatesModal}
          onClose={() => setOpenUpdatesModal(false)}
          fullWidth
          maxWidth="md"
          TransitionComponent={Transition}
        >
          <ReleaseUpdates
            onClose={() => setOpenUpdatesModal(false)}
            newUpdatesAvailable={newUpdatesAvailable}
          />
        </Dialog>

        <Dialog
          open={openAdminModal}
          onClose={() => setOpenAdminModal(false)}
          fullScreen
          TransitionComponent={Transition}
        >
          <AdminManagement onClose={() => setOpenAdminModal(false)} />
        </Dialog>
      </>
    );
  else
    return (
      <div
        className={
          "home-section home-section-container env-prodcolor height100"
        }
      >
        <div className="flex-display align-center">
          <Box
            component="button"
            className="logo-section cursor-pointer"
            onClick={() => navigate(`${props.logoURL}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                navigate(`${props.logoURL}`);
              }
            }}
            aria-label="Navigate to home"
            sx={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <Box
              component="img"
              alt="logo"
              src={logo}
              sx={{
                width: "75px",
              }}
            />
          </Box>

          <span className={classes.title}>{props.title}</span>
        </div>
        <div className={classes.menuAndAccountContainer}>
          <HeaderMenu />
          <UserAccount />
        </div>
        <Snackbar snack={snack} snackReset={snackReset} />
      </div>
    );
};

Header.propTypes = {
  version: PropTypes.string,
  title: PropTypes.string,
  logoURL: PropTypes.string,
};

export default Header;
